'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { calcularMargemLucro } from '@/lib/calculos/margem-lucro'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  SalvarCalculoCta,
  Eyebrow,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  SoftExportBar,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoPayload } from '@/lib/export/types'
import { cn } from '@/lib/utils'

type Modo = 'liquida' | 'bruta'

export function MargemClient() {
  const [receita, setReceita] = useState('')
  const [custos, setCustos] = useState('')

  // Pré-preenche com o que veio do console da home (?receita=&custo=).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const r = p.get('receita')
    const c = p.get('custo')
    if (r) setReceita(r)
    if (c) setCustos(c)
  }, [])
  const [modo, setModo] = useState<Modo>('liquida')

  const r = useMemo(
    () => calcularMargemLucro({ receita: parseValor(receita), custos: parseValor(custos) }),
    [receita, custos]
  )

  const margemAnim = useAnimatedNumber(r.margem)

  useCalcTelemetry(
    'margem_lucro',
    parseValor(receita) > 0,
    `Margem de ${r.margem.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
  )

  const buildCartao = (): CartaoPayload => ({
    nome: 'margem-de-lucro',
    heroLabel: r.prejuizo ? 'Você está no prejuízo' : 'Sua margem de lucro',
    heroValue: `${r.margem.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
    tone: r.prejuizo ? 'neg' : 'brand',
    linhas: [
      { label: 'Receita', value: brl(r.receita) },
      { label: 'Custos', value: brl(r.custos) },
      {
        label: r.prejuizo ? 'Falta de cada venda' : 'Sobra de cada venda',
        value: `${r.lucro < 0 ? '− ' : ''}${brl(Math.abs(r.lucro))}`,
        strong: true,
        tone: r.prejuizo ? 'neg' : 'brand',
      },
    ],
  })

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <MoneyField
          label="Quanto você vendeu? (receita)"
          value={receita}
          onChange={setReceita}
          hint="O preço de venda ou o total que entrou."
        />

        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-ink">Quanto custou pra vender?</span>
          <div className="inline-flex border border-line">
            <ModoTab active={modo === 'liquida'} onClick={() => setModo('liquida')}>
              Líquida
            </ModoTab>
            <ModoTab active={modo === 'bruta'} onClick={() => setModo('bruta')}>
              Bruta
            </ModoTab>
          </div>
        </div>
        <MoneyField
          label=""
          value={custos}
          onChange={setCustos}
          hint={
            modo === 'liquida'
              ? 'Margem líquida: todos os custos e despesas (material, taxa, frete, etc.).'
              : 'Margem bruta: só o custo direto do que você vendeu.'
          }
        />
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="trend" tone="muted" className="mb-3">
          {modo === 'liquida' ? 'Sua margem líquida' : 'Sua margem bruta'}
        </Eyebrow>

        <ResultCard
          tone={r.prejuizo ? 'neg' : 'brand'}
          heroLabel={r.prejuizo ? 'Você está no prejuízo' : 'Sua margem de lucro'}
          heroValue={
            r.calculavel
              ? `${margemAnim.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
              : '—'
          }
        >
          {!r.calculavel ? (
            <p className="py-2 text-[13px] text-ink-mute">Informe a receita pra calcular a margem.</p>
          ) : (
            <>
              <DetailRow label="Receita" value={brl(r.receita)} />
              <DetailRow label="Custos" value={brl(r.custos)} />
              <RowStrong
                label={r.prejuizo ? 'Falta de cada venda' : 'Sobra de cada venda'}
                value={`${r.lucro < 0 ? '− ' : ''}${brl(Math.abs(r.lucro))}`}
                tone={r.prejuizo ? 'neg' : 'brand'}
              />
              <p className="mt-1.5 font-mono text-[11.5px] text-ink-faint">
                seu ganho é {r.markup.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}% sobre o custo
              </p>
            </>
          )}
        </ResultCard>

        {r.prejuizo && (
          <AvisoBox tone="neg" icon="alert" className="mt-4">
            Seus custos estão maiores que a receita — cada venda dá prejuízo. Reveja o preço ou
            corte custos antes de vender mais.
          </AvisoBox>
        )}

        <SoftExportBar buildCartao={buildCartao} />
        <LeadCapture show={r.calculavel} source="margem-de-lucro" alertType="das" />
        <SalvarCalculoCta
          show={r.calculavel}
          source="margem-de-lucro"
          hook="Margem na mão. No Premium você vê o mês inteiro — teto, DAS e orçamento."
          calcData={
            r.calculavel && r.receita > 0 && r.custos > 0
              ? {
                  tipo: 'MARGEM_LUCRO',
                  inputs: { precoVenda: r.receita, custoTotal: r.custos },
                  resultado: {
                    margemBruta: r.margem,
                    margemLiquida: r.margem,
                    lucroBruto: r.lucro,
                    markup: r.markup,
                  },
                  titulo: 'Margem de lucro',
                }
              : undefined
          }
        />
      </section>

      {r.calculavel && <MobileResultBar payload={buildCartao()} cardRef={cardRef} />}
    </div>
  )
}

function ModoTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-[12.5px] font-medium',
        active ? 'bg-brand text-white' : 'bg-surface text-ink-mute hover:text-ink'
      )}
    >
      {children}
    </button>
  )
}
